/**
 * BNESIM Customer Care (CC) API client for the website chat. Contract:
 * ai_docs/specs/customer-care-chat.md (Luca's Flutter reference, 2026-08-14).
 *
 * Every call after login carries `Authorization: Bearer <accessToken>`.
 * There is no refresh token: a 401 means "log in again with the PTS token",
 * which the store does once and replays the call.
 */

import { getCcApiBaseUrl, getCcSocketUrl } from "@utilities/base-url";

export interface CcUser {
  id: string | number;
  firstName?: string;
  lastName?: string;
  email?: string;
  profileImage?: string;
  /** `customer`, `agent`, `bot`, … (labels only; the server decides scope). */
  userType?: string;
}

/** `[filename, url]` — filename FIRST, url second (easy to get backwards). */
export type CcAttachment = [string, string];

export interface CcMessage {
  id: string | number;
  userId: string | number;
  message: string;
  creationTime: string;
  statusCode?: number;
  attachments?: CcAttachment[];
  payload?: unknown;
  conversationId: string | number;
}

export interface CcConversation {
  id: string | number;
  title?: string;
  creationTime: string;
  statusCode: number;
  /** "closed" once the agent has closed the conversation. */
  workflowStatus?: string;
  department?: number;
  source?: string;
  sourceName?: string;
  tags?: string[];
  agentId?: string | number | null;
  user?: CcUser | null;
  agent?: CcUser | null;
  lastMessage?: CcMessage | null;
  unreadCount?: number;
}

export interface CcConversationDetail extends CcConversation {
  messages: CcMessage[];
  senders?: Record<string, CcUser>;
}

export interface CcProfile {
  accessToken: string;
  userId: string | number;
  firstName?: string;
  lastName?: string;
  email?: string;
}

export class CcError extends Error {
  status: number;
  code: string;
  constructor(status: number, code: string, message?: string) {
    super(message ?? code);
    this.name = "CcError";
    this.status = status;
    this.code = code;
  }
}

/** The department new customer conversations go to: 4 (mobile team,
 *  2026-09-30; Luca's note said 1) unless the brand sets its own
 *  (Kaspersky: 2). */
const configuredDepartment = Number(process.env.GATSBY_CC_DEPARTMENT);
export const CC_DEPARTMENT =
  Number.isInteger(configuredDepartment) && configuredDepartment > 0
    ? configuredDepartment
    : 4;

// cc.bnesim.com, or cc.bne.li on bne.li (utilities/base-url.ts).
export const ccApiBase = (): string => getCcApiBaseUrl().replace(/\/+$/, "");
export const ccSocketUrl = (): string => getCcSocketUrl().replace(/\/+$/, "");
export const isCcConfigured = (): boolean => ccApiBase() !== "";

/** The API wraps single objects in `{ success, data }`; lists come as-is. */
const unwrap = <T>(json: unknown): T =>
  json !== null &&
  typeof json === "object" &&
  "data" in json &&
  !Array.isArray((json as { data: unknown }).data) &&
  !("page" in json)
    ? (json as { data: T }).data
    : (json as T);

const request = async <T>(
  path: string,
  init: RequestInit,
  token?: string,
): Promise<T> => {
  const headers = new Headers(init.headers);
  if (token !== undefined) headers.set("Authorization", `Bearer ${token}`);
  if (!(init.body instanceof FormData) && init.body !== undefined) {
    headers.set("Content-Type", "application/json");
  }
  headers.set("Accept", "application/json");
  const response = await fetch(`${ccApiBase()}${path}`, { ...init, headers });
  const text = await response.text();
  let json: unknown = null;
  try {
    json = text === "" ? null : JSON.parse(text);
  } catch {
    json = null;
  }
  if (!response.ok) {
    const body = (json ?? {}) as {
      code?: string;
      message?: string;
      error?: string | { code?: string; message?: string };
    };
    const err = typeof body.error === "object" ? body.error : undefined;
    throw new CcError(
      response.status,
      err?.code ??
        body.code ??
        (typeof body.error === "string" ? body.error : undefined) ??
        `HTTP_${response.status}`,
      err?.message ?? body.message,
    );
  }
  return unwrap<T>(json);
};

export const ccLogin = async (licenseToken: string): Promise<CcProfile> =>
  await request<CcProfile>("/customer-auth/login", {
    method: "POST",
    body: JSON.stringify({ license_token: licenseToken }),
  });

export interface CcPage<T> {
  data: T[];
  page?: number;
  limit?: number;
  total?: number;
}

export const ccListConversations = async (
  token: string,
  page = 1,
  limit = 100,
): Promise<CcConversation[]> => {
  const params = new URLSearchParams({
    page: String(page),
    limit: String(limit),
  });
  const result = await request<CcPage<CcConversation> | CcConversation[]>(
    `/conversations?${params.toString()}`,
    { method: "GET" },
    token,
  );
  return Array.isArray(result) ? result : (result.data ?? []);
};

export const ccNewConversations = async (
  token: string,
  since: string,
): Promise<CcConversation[]> => {
  const result = await request<CcPage<CcConversation> | CcConversation[]>(
    `/conversations/new?since=${encodeURIComponent(since)}`,
    { method: "GET" },
    token,
  );
  return Array.isArray(result) ? result : (result.data ?? []);
};

export const ccGetConversation = async (
  token: string,
  id: string | number,
): Promise<CcConversationDetail> =>
  await request<CcConversationDetail>(
    `/conversations/${id}`,
    { method: "GET" },
    token,
  );

export const ccNewMessages = async (
  token: string,
  id: string | number,
  since: string,
): Promise<CcMessage[]> => {
  const result = await request<CcPage<CcMessage> | CcMessage[]>(
    `/conversations/${id}/messages/new?since=${encodeURIComponent(since)}`,
    { method: "GET" },
    token,
  );
  return Array.isArray(result) ? result : (result.data ?? []);
};

export const ccCreateConversation = async (
  token: string,
  title?: string,
): Promise<{ id: string | number }> =>
  await request<{ id: string | number }>(
    "/conversations",
    {
      method: "POST",
      body: JSON.stringify({
        ...(title !== undefined && title !== "" ? { title } : {}),
        department: CC_DEPARTMENT,
      }),
    },
    token,
  );

export const ccSendMessage = async (
  token: string,
  id: string | number,
  message: string,
  attachments: CcAttachment[] = [],
): Promise<CcMessage> =>
  await request<CcMessage>(
    `/conversations/${id}/messages`,
    {
      method: "POST",
      body: JSON.stringify(
        attachments.length > 0 ? { message, attachments } : { message },
      ),
    },
    token,
  );

export const ccUpload = async (
  token: string,
  file: File,
): Promise<{ url: string }> => {
  const body = new FormData();
  body.append("file", file, file.name);
  return await request<{ url: string }>(
    "/upload",
    { method: "POST", body },
    token,
  );
};

export const ccDeleteUpload = async (
  token: string,
  url: string,
): Promise<void> => {
  await request<unknown>(
    "/upload",
    { method: "DELETE", body: JSON.stringify({ url }) },
    token,
  );
};

export const ccRate = async (
  token: string,
  id: string | number,
  rating: number,
  agentId?: string | number | null,
): Promise<void> => {
  await request<unknown>(
    `/conversations/${id}/rating`,
    {
      method: "POST",
      body: JSON.stringify(
        agentId !== undefined && agentId !== null
          ? { rating, agent_id: agentId }
          : { rating },
      ),
    },
    token,
  );
};

/** Absolute URL of an attachment: the API returns a relative path or an S3 URL. */
export const ccAttachmentUrl = (url: string): string =>
  /^https?:\/\//.test(url)
    ? url
    : `${ccSocketUrl()}/${url.replace(/^\/+/, "")}`;

export const isImageAttachment = (filename: string): boolean =>
  /\.(png|jpe?g|gif|webp|avif|svg)$/i.test(filename);
